import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, ThermometerSnowflake, Ruler, Calendar, Tag } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { Reveal, ChapterTag, SectionTitle, EASE } from "./Reveal";

const CATEGORIES = ["Semua", "Cold Storage", "Freezer Room", "Chiller Room", "Blast Freezer", "Refrigeration", "Maintenance"];

export function Portfolio() {
  const { content, wa } = useSite();
  const items = content?.portfolio || [];
  const [filter, setFilter] = useState("Semua");
  const [selected, setSelected] = useState(null);

  const filtered = filter === "Semua" ? items : items.filter((p) => p.category === filter);

  return (
    <section id="portofolio" data-testid="portfolio-section" className="relative py-24 sm:py-32 bg-navy-900 blueprint-grid">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <ChapterTag number="06" label="Portofolio" />
        <div className="mb-10">
          <SectionTitle>Proyek yang Telah <span className="text-ice">Kami Kerjakan</span></SectionTitle>
        </div>

        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-2.5 mb-10" data-testid="portfolio-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`font-mono text-xs tracking-wider uppercase px-4 py-2.5 rounded-sm border transition-colors duration-200 ${
                  filter === c ? "bg-ice text-navy-950 border-ice font-semibold" : "border-glacier/25 text-steel hover:border-ice/60 hover:text-ice"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.article
                layout
                key={p.id || p.title}
                data-testid={`portfolio-card-${i}`}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="group relative h-72 rounded-sm overflow-hidden border border-glacier/15 cursor-pointer"
                onClick={() => setSelected(p)}
              >
                {p.image && <img src={p.image} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-ice uppercase">{p.category}</span>
                  <h3 className="mt-1.5 font-display text-2xl font-semibold uppercase tracking-wide text-frost leading-tight">{p.title}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-steel">
                    <MapPin className="w-3.5 h-3.5 text-glacier" /> {p.location} — {p.year}
                  </p>
                  <span className="mt-3 inline-block text-xs font-semibold text-ice opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                    Lihat Detail Proyek →
                  </span>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <p className="text-center text-steel py-16" data-testid="portfolio-empty">Belum ada proyek pada kategori ini.</p>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="portfolio-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-navy-950/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="bg-navy-900 border border-glacier/25 rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-64">
                {selected.image && <img src={selected.image} alt={selected.title} className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900 to-transparent" />
                <button data-testid="portfolio-modal-close" onClick={() => setSelected(null)} className="absolute top-4 right-4 w-9 h-9 rounded-sm bg-navy-950/80 border border-glacier/25 flex items-center justify-center text-frost hover:text-ice" aria-label="Tutup">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-7">
                <span className="font-mono text-[10px] tracking-[0.25em] text-ice uppercase">{selected.category}</span>
                <h3 className="mt-2 font-display text-3xl font-bold uppercase text-frost" data-testid="portfolio-modal-title">{selected.title}</h3>
                <p className="mt-3 text-sm text-steel leading-relaxed">{selected.description}</p>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {[
                    { icon: MapPin, label: "Lokasi", value: selected.location },
                    { icon: Tag, label: "Jenis", value: selected.type },
                    { icon: Ruler, label: "Ukuran", value: selected.size },
                    { icon: ThermometerSnowflake, label: "Temperatur", value: selected.temperature },
                    { icon: Calendar, label: "Tahun", value: selected.year },
                  ].map((d) => (
                    <div key={d.label} className="flex items-start gap-3 border border-glacier/15 rounded-sm p-3.5">
                      <d.icon className="w-4 h-4 text-ice mt-0.5 shrink-0" />
                      <div>
                        <p className="font-mono text-[10px] tracking-[0.2em] text-steel uppercase">{d.label}</p>
                        <p className="text-sm text-frost mt-0.5">{d.value || "-"}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <a
                  data-testid="portfolio-modal-cta"
                  href={wa(`Halo, saya tertarik dengan proyek serupa "${selected.title}". Mohon info lebih lanjut.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 flex items-center justify-center gap-2 bg-ice text-navy-950 font-semibold px-6 py-3.5 rounded-sm hover:bg-glacier transition-colors w-full"
                >
                  Diskusikan Proyek Serupa
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
